import type { ViewportSnapshot } from "../../contracts/game";

const MIN_RENDER_SCALE = 1;
const MAX_RENDER_SCALE = 1.5;

function isFinitePositive(value: number | undefined): value is number {
  return value !== undefined && Number.isFinite(value) && value > 0;
}

export function clampRenderScale(value: number): number {
  if (!Number.isFinite(value)) return MIN_RENDER_SCALE;
  return Math.min(MAX_RENDER_SCALE, Math.max(MIN_RENDER_SCALE, value));
}

export function readViewportSnapshot(scope: Window, renderScale = 1): ViewportSnapshot {
  const visual = scope.visualViewport;
  const width = isFinitePositive(visual?.width)
    ? visual.width
    : isFinitePositive(scope.innerWidth)
      ? scope.innerWidth
      : 1;
  const height = isFinitePositive(visual?.height)
    ? visual.height
    : isFinitePositive(scope.innerHeight)
      ? scope.innerHeight
      : 1;
  const devicePixelRatio = isFinitePositive(scope.devicePixelRatio)
    ? scope.devicePixelRatio
    : 1;

  return {
    width,
    height,
    devicePixelRatio,
    renderScale: clampRenderScale(renderScale),
  };
}

export type ViewportListener = (snapshot: ViewportSnapshot) => void;

export class ViewportService {
  private readonly listeners = new Set<ViewportListener>();
  private current: ViewportSnapshot;
  private renderScale: number;
  private scheduledFrame: number | null = null;
  private destroyed = false;

  private readonly handleGeometryChange = (): void => {
    if (this.destroyed || this.scheduledFrame !== null) return;
    this.scheduledFrame = this.scope.requestAnimationFrame(() => {
      this.scheduledFrame = null;
      if (this.destroyed) return;
      this.current = readViewportSnapshot(this.scope, this.renderScale);
      const snapshot = { ...this.current };
      for (const listener of this.listeners) listener(snapshot);
    });
  };
  constructor(
    private readonly scope: Window,
    renderScale = 1,
  ) {
    this.renderScale = clampRenderScale(renderScale);
    this.current = readViewportSnapshot(scope, this.renderScale);
    scope.addEventListener("resize", this.handleGeometryChange);
    scope.addEventListener("orientationchange", this.handleGeometryChange);
    scope.visualViewport?.addEventListener("resize", this.handleGeometryChange);
  }

  get snapshot(): ViewportSnapshot {
    return { ...this.current };
  }

  subscribe(listener: ViewportListener): () => void {
    if (this.destroyed) return () => undefined;
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  setRenderScale(renderScale: number): void {
    const next = clampRenderScale(renderScale);
    if (next === this.renderScale) return;
    this.renderScale = next;
    this.handleGeometryChange();
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.scope.removeEventListener("resize", this.handleGeometryChange);
    this.scope.removeEventListener("orientationchange", this.handleGeometryChange);
    this.scope.visualViewport?.removeEventListener("resize", this.handleGeometryChange);
    if (this.scheduledFrame !== null) {
      this.scope.cancelAnimationFrame(this.scheduledFrame);
      this.scheduledFrame = null;
    }
    this.listeners.clear();
  }
}
